import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import {
    useUpdateClaim,
    type Claim,
    type ClaimFrequency,
    type ClaimWritePayload,
    type DxPointers,
} from '@/api/claims';
import { useServiceCodes } from '@/api/serviceCodes';
import { SelectField } from '../clients/SelectField';

/*
 * Edit dialog for an existing claim. Scoped to the CMS-1500 fields that the
 * user is allowed to override post-auto-creation: service code + mods, POS,
 * units / rate / billed amount, diagnoses, auth #, payer + payer ID, and
 * notes. Payment fields (paid, allowed, PR, payment date, payer ref) are
 * intentionally NOT here — those belong to RecordPaymentDialog so the ERA
 * path is the single source of truth for how money flows in.
 */

const POS_OPTIONS: Array<{ value: string; label: string }> = [
    { value: '02', label: 'Telehealth (02)' },
    { value: '10', label: 'Telehealth — Patient Home (10)' },
    { value: '11', label: 'Office (11)' },
    { value: '12', label: 'Home (12)' },
    { value: '99', label: 'Other (99)' },
];

interface ClaimEditDialogProps {
    claim: Claim;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface FormState {
    serviceCode: string;
    mod1: string;
    mod2: string;
    mod3: string;
    mod4: string;
    pos: string;
    units: string;
    unitRate: string;
    amount: string;
    payer: string;
    payerId: string;
    authNumber: string;
    referralNumber: string;
    frequency: ClaimFrequency;
    originalClaimNumber: string;
    dx1: string;
    dx2: string;
    dxPointers: DxPointers;
    serviceStartTime: string;
    serviceEndTime: string;
    notes: string;
}

function toForm(c: Claim): FormState {
    return {
        serviceCode: c.serviceCode ?? '',
        mod1: c.mod1 ?? '',
        mod2: c.mod2 ?? '',
        mod3: c.mod3 ?? '',
        mod4: c.mod4 ?? '',
        pos: c.pos ?? '11',
        units: c.units ? String(c.units) : '',
        unitRate: c.unitRate ? String(c.unitRate) : '',
        amount: c.amount ? String(c.amount) : '',
        payer: c.payer ?? '',
        payerId: c.payerId ?? '',
        authNumber: c.authNumber ?? '',
        referralNumber: c.referralNumber ?? '',
        frequency: c.frequency ?? 'original',
        originalClaimNumber: c.originalClaimNumber ?? '',
        dx1: c.dx1 ?? '',
        dx2: c.dx2 ?? '',
        dxPointers: c.dxPointers ?? 'A',
        serviceStartTime: c.serviceStartTime ?? '',
        serviceEndTime: c.serviceEndTime ?? '',
        notes: c.notes ?? '',
    };
}

export function ClaimEditDialog({ claim, open, onOpenChange }: ClaimEditDialogProps) {
    const mutation = useUpdateClaim();
    const { data: serviceCodes } = useServiceCodes();
    const [form, setForm] = useState<FormState>(() => toForm(claim));

    useEffect(() => {
        if (open) {
            setForm(toForm(claim));
            mutation.reset();
        }
    }, [open, claim.id]); // eslint-disable-line react-hooks/exhaustive-deps

    function set<K extends keyof FormState>(key: K, value: FormState[K]) {
        setForm((f) => ({ ...f, [key]: value }));
    }

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        const units = Number(form.units) || 0;
        const rate = Number(form.unitRate) || 0;
        const amount = Number(form.amount) || 0;
        const payload: ClaimWritePayload = {
            serviceCode: form.serviceCode.trim(),
            mod1: form.mod1.trim(),
            mod2: form.mod2.trim(),
            mod3: form.mod3.trim(),
            mod4: form.mod4.trim(),
            pos: form.pos,
            units,
            unitRate: rate,
            amount,
            payer: form.payer.trim(),
            payerId: form.payerId.trim(),
            authNumber: form.authNumber.trim(),
            referralNumber: form.referralNumber.trim(),
            frequency: form.frequency,
            originalClaimNumber: form.originalClaimNumber.trim(),
            dx1: form.dx1.trim(),
            dx2: form.dx2.trim(),
            dxPointers: form.dxPointers,
            serviceStartTime: form.serviceStartTime.trim(),
            serviceEndTime: form.serviceEndTime.trim(),
            notes: form.notes,
        };
        try {
            await mutation.mutateAsync({ id: claim.id, payload });
            onOpenChange(false);
        } catch {
            /* surfaced below via mutation.error */
        }
    }

    const serverError = (() => {
        const e = mutation.error;
        if (!e) return null;
        if (e instanceof AxiosError) return e.response?.data?.error ?? 'Failed to save claim';
        return 'Unexpected error';
    })();

    const activeCodes = (serviceCodes ?? []).filter((c) => c.status === 'Active');

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[min(calc(100vw-2rem),44rem)]">
                <DialogHeader>
                    <DialogTitle>Edit Claim {claim.claimNo}</DialogTitle>
                    <DialogDescription>
                        Update CMS-1500 fields. Payment entries are recorded separately via Record Payment.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={onSubmit} noValidate>
                    {serverError && <Alert variant="danger" className="mb-4">{serverError}</Alert>}

                    <div className="grid grid-cols-2 gap-x-4">
                        <FormField label="CPT / Service Code" required>
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={form.serviceCode}
                                    onChange={(e) => set('serviceCode', e.target.value)}
                                >
                                    <option value="">Select…</option>
                                    {activeCodes.map((c) => (
                                        <option key={c.id} value={c.code}>
                                            {c.code} — {c.desc}
                                        </option>
                                    ))}
                                    {form.serviceCode && !activeCodes.find((c) => c.code === form.serviceCode) && (
                                        <option value={form.serviceCode}>
                                            {form.serviceCode} (current)
                                        </option>
                                    )}
                                </SelectField>
                            )}
                        </FormField>

                        <FormField label="Place of Service" required>
                            {(id) => (
                                <SelectField id={id} value={form.pos} onChange={(e) => set('pos', e.target.value)}>
                                    {POS_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>

                        <div className="grid grid-cols-4 gap-x-2 col-span-2">
                            <FormField label="Mod 1">
                                {(id) => <Input id={id} value={form.mod1} maxLength={2} onChange={(e) => set('mod1', e.target.value.toUpperCase())} />}
                            </FormField>
                            <FormField label="Mod 2">
                                {(id) => <Input id={id} value={form.mod2} maxLength={2} onChange={(e) => set('mod2', e.target.value.toUpperCase())} />}
                            </FormField>
                            <FormField label="Mod 3">
                                {(id) => <Input id={id} value={form.mod3} maxLength={2} onChange={(e) => set('mod3', e.target.value.toUpperCase())} />}
                            </FormField>
                            <FormField label="Mod 4">
                                {(id) => <Input id={id} value={form.mod4} maxLength={2} onChange={(e) => set('mod4', e.target.value.toUpperCase())} />}
                            </FormField>
                        </div>

                        <FormField label="Units" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    min={0}
                                    step="1"
                                    value={form.units}
                                    onChange={(e) => set('units', e.target.value)}
                                />
                            )}
                        </FormField>
                        <FormField label="Unit Rate" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={form.unitRate}
                                    onChange={(e) => set('unitRate', e.target.value)}
                                />
                            )}
                        </FormField>

                        <FormField
                            label="Billed Amount"
                            required
                            hint="Practices bill above the allowed amount; insurance adjusts down to allowed."
                            className="col-span-2"
                        >
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={form.amount}
                                    onChange={(e) => set('amount', e.target.value)}
                                />
                            )}
                        </FormField>

                        <FormField label="Service Start Time" hint="24-hour HH:MM; used on 837P.">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="time"
                                    value={form.serviceStartTime}
                                    onChange={(e) => set('serviceStartTime', e.target.value)}
                                />
                            )}
                        </FormField>
                        <FormField label="Service End Time" hint="24-hour HH:MM; used on 837P.">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="time"
                                    value={form.serviceEndTime}
                                    onChange={(e) => set('serviceEndTime', e.target.value)}
                                />
                            )}
                        </FormField>

                        <FormField label="Payer">
                            {(id) => <Input id={id} value={form.payer} onChange={(e) => set('payer', e.target.value)} />}
                        </FormField>
                        <FormField label="Payer ID (EDI)">
                            {(id) => <Input id={id} value={form.payerId} onChange={(e) => set('payerId', e.target.value)} />}
                        </FormField>

                        <FormField label="Authorization # (REF*G1)">
                            {(id) => <Input id={id} value={form.authNumber} onChange={(e) => set('authNumber', e.target.value)} />}
                        </FormField>
                        <FormField label="Referral # (REF*9F)">
                            {(id) => <Input id={id} value={form.referralNumber} onChange={(e) => set('referralNumber', e.target.value)} />}
                        </FormField>

                        <FormField
                            label="Claim frequency"
                            hint="Original, corrected replacement, or void of a prior claim."
                        >
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={form.frequency}
                                    onChange={(e) => set('frequency', e.target.value as ClaimFrequency)}
                                >
                                    <option value="original">Original (1)</option>
                                    <option value="corrected">Corrected (7)</option>
                                    <option value="void">Void (8)</option>
                                </SelectField>
                            )}
                        </FormField>
                        <FormField
                            label="Original claim # (REF*F8)"
                            hint="Required when frequency is Corrected or Void."
                        >
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.originalClaimNumber}
                                    onChange={(e) => set('originalClaimNumber', e.target.value)}
                                    disabled={form.frequency === 'original'}
                                />
                            )}
                        </FormField>

                        <FormField label="Diagnosis — Primary (ICD-10)">
                            {(id) => <Input id={id} value={form.dx1} onChange={(e) => set('dx1', e.target.value.toUpperCase())} />}
                        </FormField>
                        <FormField label="Diagnosis — Secondary (ICD-10)">
                            {(id) => <Input id={id} value={form.dx2} onChange={(e) => set('dx2', e.target.value.toUpperCase())} />}
                        </FormField>

                        <FormField
                            label="Diagnosis pointer"
                            hint="Which diagnosis applies to this service line (CMS-1500 Box 24E)."
                            className="col-span-2"
                        >
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={form.dxPointers}
                                    onChange={(e) => set('dxPointers', e.target.value as DxPointers)}
                                >
                                    <option value="A">A — Primary only</option>
                                    <option value="B" disabled={!form.dx2.trim()}>B — Secondary only</option>
                                    <option value="AB" disabled={!form.dx2.trim()}>A + B — Both</option>
                                </SelectField>
                            )}
                        </FormField>

                        <FormField label="Notes" className="col-span-2">
                            {(id) => (
                                <Textarea
                                    id={id}
                                    rows={3}
                                    placeholder="Internal billing notes (not sent on the claim)"
                                    value={form.notes}
                                    onChange={(e) => set('notes', e.target.value)}
                                />
                            )}
                        </FormField>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={mutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={mutation.isPending}>
                            {mutation.isPending ? 'Saving…' : 'Save changes'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
